import { useEffect } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useAuthUser } from "@/hooks/useAuthUser";
import { RTStoreKit, iapAvailable } from "@/lib/native-iap";
import { syncApplePurchases } from "@/lib/apple-iap.functions";

/**
 * Keeps an Apple-billed membership in step with Apple: checks the phone's
 * current Apple subscriptions when a member opens the app, and whenever
 * StoreKit reports a renewal or a purchase approved later.
 * Server notifications do the same from Apple's side.
 */
export function AppleIapSync() {
  const user = useAuthUser();
  const sync = useServerFn(syncApplePurchases);

  useEffect(() => {
    if (!user || !iapAvailable()) return;
    let handle: { remove: () => void } | undefined;
    const push = async (ids: string[]) => {
      if (!ids.length) return;
      try {
        const r = await sync({ data: { transactionIds: ids.slice(0, 20) } });
        if (r.ok) window.dispatchEvent(new Event("rt-membership-refresh"));
      } catch {
        /* quiet: the server notification path still keeps things in step */
      }
    };
    void RTStoreKit.currentEntitlements().then((r) => push(r.transactionIds)).catch(() => {});
    void RTStoreKit.addListener("transactionUpdated", (d) => void push([d.transactionId])).then(
      (h) => (handle = h),
    );
    return () => handle?.remove();
  }, [user, sync]);

  return null;
}
